import * as React from 'react';
import {TextStyle, View, ViewStyle} from 'react-native';
import {noop} from 'lodash';
import {isEmpty, isNil, negate, overEvery} from 'lodash/fp';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TextInput, {pickTextInputProps} from '/component/base/TextInput';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {handleBiometricAuth} from '/component/screen/Login/LoginForm/LoginForm.util';
import platformConfig from '/config/platform.config';
import {Login} from '/constant/TestIDs';
import {LOGIN_TYPE} from '/enum/biometrics.enum';
import {withTheme} from '/provider/ThemeProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {gallery, malibu, salmonPink} from '/theme/color/secondary.color';
import {withHOC} from '/util/hoc.util';
import testProperties from '/util/testing/testProperties';
import {getInputFontFamily} from '/util/text.util';

import {styles} from './FormField.style';
import {getHandler, setNativeInputStyle} from './FormField.util';

const hasValue = overEvery([negate(isNil), negate(isEmpty)]);
interface BiometricButtonData {
  promptMessage?: string;
  iconName?: string;
  title?: string;
}
interface FormFieldStyles {
  base?: ViewStyle;
  focused?: ViewStyle;
  labelContainer?: ViewStyle;
  showHideButtonContainer?: ViewStyle;
  showButton?: TextStyle;
  textInputWithShowButton?: ViewStyle;
  textInput?: TextStyle;
  itemIcon?: ViewStyle;
  biometricContainer?: ViewStyle;
}
interface FormFieldProps {
  getTheme: () => unknown;
  style: FormFieldStyles;
  disabled: boolean;
  hasError: boolean;
  label: React.ReactNode;
  icon: string;
  assistiveText: React.ReactNode;
  hasBiometrics: boolean;
  handleOnSubmit: (type?: string) => void;
  setValue: (value: string) => void;
  buttonData: BiometricButtonData;
  hasShowSecureText: boolean;
  onClear: () => void;
  onChangeText: (text: string) => void;
  ts: (key: string, fallback?: string) => string;
  testID: string;
  accessibilityLabel: string;
  value: string;
  // eslint-disable-next-line react/require-default-props
  webID?: string;
}
interface FormFieldState {
  isFocused: boolean;
  isSecureText: boolean;
}
export class FormField extends React.PureComponent<
  FormFieldProps,
  FormFieldState
> {
  inputEl: any;

  constructor(props: FormFieldProps) {
    super(props);

    this.state = {
      isFocused: false,
      isSecureText: props.hasShowSecureText ?? false,
    };

    this.handleTextInputFocus = this.handleTextInputFocus.bind(this);
    this.handleTextInputBlur = this.handleTextInputBlur.bind(this);

    this.inputEl = React.createRef();
  }

  handleTextInputFocus() {
    this.setState({isFocused: true});
  }

  handleTextInputBlur() {
    this.setState({isFocused: false});
  }

  changeIsSecureText = (event?: any) => {
    event?.preventDefault();
    this.setState(prevState => ({isSecureText: !prevState.isSecureText}));
  };

  handleContainerPress = () => this.inputEl.current?.focus();

  renderTextInputWithDivider() {
    const {
      style: styleProp = {},
      disabled = false,
      hasShowSecureText = false,
      testID,
      accessibilityLabel,
      hasBiometrics,
      buttonData = {},
      setValue = noop,
      webID,
    } = this.props;

    const {isSecureText, isFocused} = this.state;

    const textInputWithShowButtonStyle = {
      ...styles.textInputWithShowButton,
      ...styleProp.textInputWithShowButton,
    };

    const textInput =
      testID === Login.loginUsernameInput || testID === Login.loginPasswordInput
        ? styles.textInputLogin
        : styles.textInput;

    const borderColor =
      testID === Login.loginUsernameInput || testID === Login.loginPasswordInput
        ? isFocused
          ? this.props.hasError
            ? salmonPink
            : malibu
          : this.props.hasError
          ? salmonPink
          : gallery
        : undefined;

    const inputStyle =
      platformConfig.getOS() === 'android' && hasShowSecureText
        ? getInputFontFamily()
        : {};
    const textInputStyle = {
      ...textInput,
      ...styleProp.textInput,
      borderColor,
      ...inputStyle,
    };
    const itemIconStyle = {
      ...styles.itemIcon,
      ...styleProp.itemIcon,
    };

    const biometricContainerStyle = {
      ...styles.biometricContainer,
      ...styleProp.biometricContainer,
    };

    return (
      <>
        <View style={textInputWithShowButtonStyle}>
          <TextInput
            accessibilityLabel={accessibilityLabel}
            testID={testID}
            webID={webID}
            ref={this.inputEl}
            style={textInputStyle}
            disabled={disabled}
            greyOutTextInputValue={disabled}
            onFocus={getHandler(
              this.handleTextInputFocus,
              'onFocus',
              this.props,
            )}
            onBlur={getHandler(this.handleTextInputBlur, 'onBlur', this.props)}
            {...pickTextInputProps(this.props)}
            secureTextEntry={isSecureText}
            allowFontScaling
            maxFontSizeMultiplier={2}
          />

          {!hasShowSecureText && hasBiometrics && !isEmpty(buttonData) && (
            <TouchableOpacity
              accessible={false}
              style={biometricContainerStyle}
              onPress={handleBiometricAuth({
                promptMessage: buttonData.promptMessage,
                setValue,
                handleOnSubmit: () => {
                  if (this.props.handleOnSubmit) {
                    this.props.handleOnSubmit(LOGIN_TYPE.biometrics);
                  }
                },
              })}
              {...testProperties(null, true)}>
              <Icon name={buttonData.iconName} style={itemIconStyle} />
            </TouchableOpacity>
          )}

          {/* {onClear && hasValue(value) ? (
            <TouchableOpacity
              accessibilityLabel={ts('accessibilityLabel', 'Clear Search')}
              onPress={onClear}
              {...testProperties(null, true)}>
              <Icon name={'close'} style={styles.clearIcon} />
            </TouchableOpacity>
          ) : null} */}
        </View>
      </>
    );
  }

  renderLabel() {
    const {
      label,
      disabled,
      hasShowSecureText,
      ts = (k, f) => f ?? k,
      style: styleProp = {},
    } = this.props;

    const labelStyle = {
      ...styles.label,
      ...(disabled ? styles.labelDisabled : {}),
    };

    const {isSecureText} = this.state;

    const showButtonStyle = {
      ...styles.showButton,
      ...styleProp.showButton,
    };

    const labelContainerStyle = {
      ...styles.labelContainer,
      ...styleProp.labelContainer,
    };

    const showHideButtonContainerStyle = {
      ...styles.showHideButtonContainer,
      ...styleProp.showHideButtonContainer,
    };

    return (
      <View style={labelContainerStyle}>
        <Text
          variant="button"
          style={labelStyle}
          accessible={true}
          accessibilityLabel={label}>
          {label}
        </Text>
        {hasShowSecureText ? (
          <TouchableOpacity
            style={showHideButtonContainerStyle}
            accessibilityLabel={ts('showAccessibilityLabel', 'Show Password')}
            onPress={e => this.changeIsSecureText(e)}>
            <Icon
              name={isSecureText ? 'eyeOpen' : 'eyeClose'}
              style={styles.showHideIcon}
            />
            <Text style={showButtonStyle} variant="button" disabled>
              {isSecureText ? ts('Show', 'Show') : ts('Hide', 'Hide')}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  renderAssistiveText() {
    const {assistiveText, disabled, hasError} = this.props;

    const assistiveTextStyle = {
      ...styles.assistiveText,
      ...(disabled ? styles.assistiveTextDisabled : {}),
      ...(hasError ? styles.assistiveTextError : {}),
    };

    return (
      <Text variant="caption" style={assistiveTextStyle}>
        {assistiveText}
      </Text>
    );
  }

  renderIcon() {
    const {icon, hasError} = this.props;

    const iconStyle = {
      ...styles.icon,
      ...(hasError ? styles.iconError : {}),
    };

    return <Icon name={icon} accessible={false} style={iconStyle} />;
  }

  // @ts-expect-error TS(4114): This member must have an 'override' modifier becau... Remove this comment to see the full error message
  componentDidMount() {
    const {hasShowSecureText} = this.props;
    setNativeInputStyle(this.inputEl, {hasShowSecureText});
  }

  // @ts-expect-error TS(4114): This member must have an 'override' modifier becau... Remove this comment to see the full error message
  render() {
    const {
      style: styleProp = {},
      icon,
      label,
      assistiveText,
      accessibilityLabel,
    } = this.props;
    const {isFocused} = this.state;

    const containerStyle = [styles.base, styleProp.base];
    if (isFocused) containerStyle.push(styleProp.focused);

    const renderedContent = (
      <>
        {hasValue(label) ? this.renderLabel() : null}
        {this.renderTextInputWithDivider()}
        {hasValue(assistiveText) ? this.renderAssistiveText() : null}
      </>
    );
    return (
      <View
        accessible={false}
        accessibilityRole="text"
        accessibilityLabel={accessibilityLabel}
        style={containerStyle}
        activeOpacity={0.9}
        onPress={this.handleContainerPress}>
        {hasValue(icon) ? (
          <View style={styles.contentRow}>
            <View style={styles.iconColumn}>{this.renderIcon()}</View>
            <View style={styles.contentColumn}>{renderedContent}</View>
          </View>
        ) : (
          renderedContent
        )}
      </View>
    );
  }
}

export default withHOC(withTranslation('base.formField'), withTheme)(FormField);
